import type { ReactNode } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, type FlexProps, theme, Typography } from 'antd';
import type { TitleProps } from 'antd/es/typography/Title';
import { t } from 'i18next';
import { PosTooltip } from 'pos-client/components/shared/General/PosTooltip.tsx';

import type { PosCardExpandProps } from './PosCard.tsx';

/**
 * Props for {@link PosCardHeader}
 */
export interface PosCardHeaderProps extends Omit<FlexProps, 'children'> {
  /** Title text */
  title?: string;
  /** Shown inline after the title (e.g. badges) */
  titleExtra?: ReactNode;
  /** Title header size - @see {@link TitleProps['level']} */
  titleSize?: TitleProps['level'];
  contentCenter?: ReactNode;
  /** Icon to display in header _(Hidden if undefined)_ */
  icon?: IconDefinition;
  /** Color of header icon */
  iconColor?: string;
  /** Component to display in top right of header */
  action?: ReactNode;
  /** Card content */
  children?: ReactNode;
  /** Subtitle text */
  description?: ReactNode;
  /** Card body flex props @see {@link FlexProps} */
  contentProps?: Omit<FlexProps, 'children'>;
  innerContentProps?: Omit<FlexProps, 'children'>;
  /** Card expand / collapse behaviour @see {@link pos-client/components/shared/Display/PosCard.tsx} */
  expand?: PosCardExpandProps;
}

/**
 * @component
 * @description Header component for {@link PosCard}
 *
 * @see {@link https://ant.design/components/card | AntD Card Component}
 * @param props {@link PosCardHeaderProps}
 * @example
 */
export const PosCardHeader = ({
  title,
  titleExtra,
  titleSize = 4,
  contentCenter,
  icon,
  iconColor,
  children,
  action,
  contentProps,
  innerContentProps,
  description,
  expand,
  ...rest
}: PosCardHeaderProps) => {
  const { token } = theme.useToken();
  const expandable = !!expand;

  return (
    <Flex
      style={{
        width: '100%',
        paddingBottom:
          expandable && !expand.controlExpand && !expand.expanded
            ? 0
            : token.paddingMD,
      }}
      align="flex-start"
      justify="space-between"
      wrap="wrap"
      gap="middle"
      {...rest}
    >
      <Flex gap="small" wrap="wrap" style={{ width: '100%' }} {...contentProps}>
        <Flex
          style={{ width: '100%' }}
          justify="space-between"
          wrap
          gap="small"
          {...innerContentProps}
        >
          <Flex gap="small" align="center">
            {icon && (
              <FontAwesomeIcon
                icon={icon}
                size="xl"
                color={iconColor ?? token.Card?.extraColor}
              />
            )}
            <Typography.Title level={titleSize} style={{ marginBottom: 0 }}>
              {title}
            </Typography.Title>
            {titleExtra}
            {expandable && expand?.expandInTitle && (
              <PosTooltip
                title={
                  expand.expanded
                    ? t('common.buttons.showLess')
                    : t('common.buttons.showMore')
                }
              >
                <Button
                  type="text"
                  icon={
                    <FontAwesomeIcon
                      icon={expand.expanded ? faChevronUp : faChevronDown}
                    />
                  }
                  style={{
                    width: 24,
                  }}
                  onClick={() =>
                    expand.onChange && expand.onChange(!expand.expanded)
                  }
                />
              </PosTooltip>
            )}
          </Flex>
          {contentCenter && contentCenter}
          <Flex>
            {action && action}
            {expandable && !expand?.expandInTitle && (
              <PosTooltip
                title={
                  expand.expanded
                    ? t('common.buttons.showLess')
                    : t('common.buttons.showMore')
                }
              >
                <Button
                  disabled={expand.disabled}
                  icon={
                    <FontAwesomeIcon
                      icon={expand.expanded ? faChevronUp : faChevronDown}
                    />
                  }
                  onClick={() =>
                    expand.onChange && expand.onChange(!expand.expanded)
                  }
                  style={{ marginLeft: action ? token.marginSM : 0 }}
                >
                  {expand.showText
                    ? expand.expanded
                      ? t('common.buttons.showLess')
                      : t('common.buttons.showMore')
                    : undefined}
                </Button>
              </PosTooltip>
            )}
          </Flex>
        </Flex>

        {description && (
          <Typography.Text style={{ marginBottom: 0 }}>
            {description}
          </Typography.Text>
        )}
        {children}
      </Flex>
    </Flex>
  );
};
